import { SelectionModel } from '@angular/cdk/collections';
import { Component, computed, inject, input, Signal, signal, ChangeDetectionStrategy } from '@angular/core';
import { GedCategory, GedDocumentType, SignatureState } from '@lib/models/generated/graphql';
import { ExtendedFileMetadata } from '@lib/models/extended-file-metadata';
import { SHARED_IMPORTS } from 'projects/minotaure-lib/src/shared-imports';
import { FilePanelService } from '@lib/components/document-viewer/file-viewer/file-panel.service';
import { LoadingService } from '@lib/services/loading.service';
import { MenuNode } from 'omnium-ui/shared';
import { DocumentsService } from '@lib/services/documents.service';
import { OuiDialogService } from 'omnium-ui/dialog';
import { UpdateDocumentDialogComponent } from '../update-document-dialog/update-document-dialog.component';
import { ConfirmationSimpleComponent } from '@lib/components/confirmation-simple/confirmation-simple.component';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { ReplaceDocumentDialogComponent } from '../replace-document-dialog/replace-document-dialog.component';

@Component({
  selector: 'app-documents-table',
  imports: [SHARED_IMPORTS],
  templateUrl: './documents-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './documents-table.component.scss',
})
export class DocumentsTableComponent {
  public filePanelService = inject(FilePanelService);
  public loadingService = inject(LoadingService);
  public documentService = inject(DocumentsService);
  public dialogService = inject(OuiDialogService);
  public snackbarService = inject(OuiSnackbarService);
  public documents = input<ExtendedFileMetadata[]>([]);
  public isAddDocumentSignatureTable = input<boolean>(false);
  public singleSelection = input<boolean>(false);
  public category = input<string>('');
  public isReadOnly = input<boolean>(false);
  public selectedDocument = signal<ExtendedFileMetadata | null>(null);
  public documentTableColumns = computed<string[]>(() => {
    if (this.isAddDocumentSignatureTable()) {
      return ['select', 'nom', 'document', 'depot'];
    } else if (this.category() === GedCategory.Contrat) {
      return ['nom', 'document', 'depot', 'contrat', 'signature', 'option'];
    } else {
      return ['nom', 'document', 'depot', 'signature', 'expiration', 'option'];
    }
  });
  public selection = new SelectionModel<ExtendedFileMetadata>(true, []);

  public toggleSelection(document: ExtendedFileMetadata): void {
    if (this.singleSelection() && !this.selection.isSelected(document)) this.selection.clear();
    this.selection.toggle(document);
  }

  public connaissanceClientDocTypes = [
    GedDocumentType.RecInfo.toString(),
    GedDocumentType.Der.toString(),
    GedDocumentType.ProfInvest.toString(),
    'DCI',
    'FPAT',
  ];
  public identityDocTypes = [GedDocumentType.Cni.toString(), GedDocumentType.Passeport.toString(), 'ARA_CONS_PJ_1', 'ARA_CONS_PJ_2'];
  public etatSignature = SignatureState;
  optionMenuItemsDelete: Signal<MenuNode[]> = computed(() => [
    {
      name: 'Voir',
      action: () => this.openFileViewer(this.selectedDocument() as ExtendedFileMetadata),
    },
    {
      name: 'Renommer',
      disabled: this.isReadOnly(),
      action: () => this.openDialogUpdateDocument(this.selectedDocument() as ExtendedFileMetadata, 'name'),
    },
    {
      name: 'Télécharger',
      action: async () => this.downloadDocument(),
    },
    {
      name: 'Supprimer',
      disabled: this.isReadOnly(),
      action: () => this.openDialogDeleteDocument(),
    },
  ]);

  optionMenuItemsSignaturePending: Signal<MenuNode[]> = computed(() => [
    {
      name: 'Voir',
      action: () => this.openFileViewer(this.selectedDocument() as ExtendedFileMetadata),
    },
    {
      name: 'Télécharger',
      action: async () => this.downloadDocument(),
    },
  ]);

  optionMenuItemsRename: Signal<MenuNode[]> = computed(() => [
    {
      name: 'Voir',
      action: () => this.openFileViewer(this.selectedDocument() as ExtendedFileMetadata),
    },
    {
      name: 'Renommer',
      disabled: this.isReadOnly(),
      action: () => this.openDialogUpdateDocument(this.selectedDocument() as ExtendedFileMetadata, 'name'),
    },
    {
      name: 'Télécharger',
      action: async () => this.downloadDocument(),
    },
  ]);

  optionMenuItemsReplace: Signal<MenuNode[]> = computed(() => [
    {
      name: 'Voir',
      action: () => this.openFileViewer(this.selectedDocument() as ExtendedFileMetadata),
    },
    {
      name: 'Renommer',
      disabled: this.isReadOnly(),
      action: () => this.openDialogUpdateDocument(this.selectedDocument() as ExtendedFileMetadata, 'name'),
    },
    {
      name: 'Télécharger',
      action: async () => this.downloadDocument(),
    },
    {
      name: 'Remplacer',
      disabled: this.isReadOnly(),
      action: () => this.openDialogReplaceDocument(this.selectedDocument() as ExtendedFileMetadata),
    },
  ]);

  public getMenuItems(document: ExtendedFileMetadata): MenuNode[] {
    if (document.etatSignature === SignatureState.Pending) {
      return this.optionMenuItemsSignaturePending();
    } else if (this.isConnaissanceClientOrIdentityDocument(document)) {
      return this.optionMenuItemsRename();
    } else if (document.isHandwrittenSignature && this.connaissanceClientDocTypes.includes(document.typeDocumentCode as string)) {
      return this.optionMenuItemsReplace();
    } else {
      return this.optionMenuItemsDelete();
    }
  }

  private isConnaissanceClientOrIdentityDocument(document: ExtendedFileMetadata): boolean {
    return (
      ((document.etatSignature !== null || document.dateSignature !== null) &&
        !document.isHandwrittenSignature &&
        this.connaissanceClientDocTypes.includes(document.typeDocumentCode as string)) ||
      this.identityDocTypes.includes(document.typeDocumentCode as string)
    );
  }

  public openFileViewer(fileMetadata: ExtendedFileMetadata): void {
    if (!this.isAddDocumentSignatureTable()) {
      this.filePanelService.openFileViewer(fileMetadata, true);
    }
  }

  public async downloadDocument(): Promise<void> {
    await this.documentService.downloadDocument({
      url: this.selectedDocument()?.url as string,
      name: this.selectedDocument()?.fileName as string,
    });
  }

  public openDialogUpdateDocument(document: ExtendedFileMetadata, updateType: 'name'): void {
    const dialogRef = this.dialogService.openDialog(UpdateDocumentDialogComponent, { document, updateType });
    dialogRef.afterClosed().subscribe(refreshData => {
      if (refreshData) {
        this.documentService.triggerRefreshData();
      }
    });
  }

  public openDialogReplaceDocument(document: ExtendedFileMetadata): void {
    const dialogRef = this.dialogService.openDialog(ReplaceDocumentDialogComponent, { previousDocument: document });
    dialogRef.afterClosed().subscribe(refreshData => {
      if (refreshData) {
        this.documentService.triggerRefreshData();
      }
    });
  }

  public openDialogDeleteDocument(): void {
    const dialogRef = this.dialogService.openDialog(ConfirmationSimpleComponent, {
      title: 'Supprimer un document',
      message: 'Souhaitez-vous réellement supprimer ce document ?',
      validateButtonLabel: 'Valider',
    });

    dialogRef.afterClosed().subscribe(async deleteDocument => {
      if (deleteDocument) {
        this.loadingService.startLoading();
        const result = await this.documentService.deleteDocument(this.selectedDocument()?.baseFileId as string);
        this.loadingService.stopLoading();
        if (result) {
          this.snackbarService.open('Le document a été supprimé', 'success');
          this.documentService.triggerRefreshData();
        } else {
          this.snackbarService.open('Erreur lors de la suppression du document', 'error');
        }
      }
    });
  }

  public rowOptionsClicked(fileMetadata: ExtendedFileMetadata): void {
    this.selectedDocument.set(fileMetadata);
  }
}
